import { useState } from 'react';
import { Package, Clock, ChevronDown, ChevronUp, CheckCircle, XCircle, Bell } from 'lucide-react';
import { DayPlanItem } from '../lib/supabase';
import { formatTime } from '../lib/scheduler';
import { ParticleBurst } from './ParticleBurst';

interface GroupCardProps {
  groupInstanceId: string;
  items: DayPlanItem[];
  onTaken: (itemIds: string[]) => Promise<void>;
  onMissed: (itemIds: string[]) => Promise<void>;
  onSnooze: (itemIds: string[], minutes: number) => Promise<void>;
  showParticles: boolean;
}

export function GroupCard({
  groupInstanceId,
  items,
  onTaken,
  onMissed,
  onSnooze,
  showParticles,
}: GroupCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (items.length === 0) return null;

  const firstItem = items[0];
  const groupSnapshot = firstItem.source_group_snapshot;
  const plannedTime = new Date(firstItem.planned_time);
  const now = new Date();
  const isPast = now > plannedTime;

  const takenCount = items.filter(item => item.status === 'TAKEN').length;
  const missedCount = items.filter(item => item.status === 'MISSED').length;
  const totalCount = items.length;

  const allTaken = takenCount === totalCount;
  const allMissed = missedCount === totalCount;
  const allSnoozed = items.every(item => item.status === 'SNOOZED');
  const isPartial = takenCount > 0 && takenCount < totalCount;
  const allPlanned = items.every(item => item.status === 'PLANNED' || item.status === 'SNOOZED');

  const getStatusColor = () => {
    if (allTaken) return 'bg-green-50 border-green-200 text-green-800';
    if (allMissed) return 'bg-red-50 border-red-200 text-red-800';
    if (allSnoozed) return 'bg-yellow-50 border-yellow-200 text-yellow-800';
    if (isPartial) return 'bg-blue-50 border-blue-200 text-blue-800';
    return 'bg-white border-gray-200 text-gray-900';
  };

  const getStatusIcon = () => {
    if (allTaken) return <CheckCircle className="text-green-600" size={20} />;
    if (allMissed) return <XCircle className="text-red-600" size={20} />;
    if (allSnoozed) return <Bell className="text-yellow-600" size={20} />;
    return <Package className="text-orange-600" size={20} />;
  };

  const getStatusText = () => {
    if (allTaken) return 'All taken';
    if (allMissed) return 'All missed';
    if (allSnoozed) return 'Snoozed';
    if (isPartial) return `${takenCount}/${totalCount} taken`;
    return `${totalCount} supplement${totalCount !== 1 ? 's' : ''}`;
  };

  const itemIds = items.map(item => item.id);

  return (
    <div
      className={`rounded-xl border-2 p-4 hover:scale-[1.01] transition-all duration-300 relative ${getStatusColor()}`}
    >
      {showParticles && <ParticleBurst />}

      <div className="flex items-start gap-3">
        <div className="bg-orange-100 rounded-lg p-2 flex-shrink-0">
          {getStatusIcon()}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex-1">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                {groupSnapshot?.name || 'Supplement Group'}
              </h3>
              <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                <Clock size={14} />
                <span className="font-medium">{formatTime(plannedTime)}</span>
                {firstItem.is_next_day && (
                  <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-xs font-medium">
                    +1 day
                  </span>
                )}
                {isPast && allPlanned && (
                  <span className="text-red-600 font-medium">Overdue</span>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-3">
            <span className="text-xs bg-white bg-opacity-60 px-2 py-1 rounded font-medium">
              {getStatusText()}
            </span>
            {firstItem.shift_minutes > 0 && (
              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded font-medium">
                +{firstItem.shift_minutes}m shifted
              </span>
            )}
          </div>

          {allPlanned && (
            <div className="flex flex-wrap gap-2 mb-3">
              <button
                onClick={() => onTaken(itemIds)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 hover:shadow-lg active:scale-95 transition-all duration-200"
              >
                All Taken
              </button>
              <button
                onClick={() => onSnooze(itemIds, 10)}
                className="px-4 py-2 bg-yellow-500 text-white rounded-lg text-sm font-medium hover:bg-yellow-600 hover:shadow-lg active:scale-95 transition-all duration-200"
              >
                +10m
              </button>
              <button
                onClick={() => onSnooze(itemIds, 30)}
                className="px-4 py-2 bg-yellow-500 text-white rounded-lg text-sm font-medium hover:bg-yellow-600 hover:shadow-lg active:scale-95 transition-all duration-200"
              >
                +30m
              </button>
              <button
                onClick={() => onMissed(itemIds)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 hover:shadow-lg active:scale-95 transition-all duration-200"
              >
                All Missed
              </button>
            </div>
          )}

          {allTaken && items[0].actual_time && (
            <p className="text-sm text-green-700 mb-3">
              All taken at {formatTime(new Date(items[0].actual_time))}
            </p>
          )}

          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
          >
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            {isExpanded ? 'Hide' : 'Show'} supplements in group
          </button>

          {isExpanded && (
            <div className="mt-3 space-y-2">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="p-3 bg-white bg-opacity-60 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-sm">{item.preset_snapshot.name}</div>
                      <div className="flex gap-2 mt-1">
                        <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                          {item.preset_snapshot.tag}
                        </span>
                        <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                          Priority {item.preset_snapshot.priority}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.status === 'TAKEN' && (
                        <CheckCircle className="text-green-600" size={18} />
                      )}
                      {item.status === 'MISSED' && (
                        <XCircle className="text-red-600" size={18} />
                      )}
                      {item.status === 'SNOOZED' && (
                        <Bell className="text-yellow-600" size={18} />
                      )}
                    </div>
                  </div>
                  {item.conflict_notes.length > 0 && (
                    <div className="mt-2 text-xs text-gray-600">
                      {item.conflict_notes.map((note, i) => (
                        <div key={i} className="flex items-start gap-1">
                          <span className="text-orange-500">•</span>
                          <span>{note}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
